import React from "react";
import ReactDOM from "react-dom/client";
import { registerSW } from "virtual:pwa-register";
import { App } from "@/App";
import { reportClientError } from "@/lib/api";
import { initializeTelemetry, recordError } from "@/lib/telemetry";
import { initFromEnvironment } from "@/lib/client-logger";
import {
  isDexieStorageError,
  markStorageDegraded,
  probeStorageCapability,
} from "@/lib/storageCapability";
import "./index.css";

// Kick IndexedDB probe early so Private browsing is flagged before first punch.
void probeStorageCapability();

// Service worker: precaches the app shell (offline loads) and replays the
// punch outbox via Background Sync where supported.
// When a new build is available, force-activate and reload once so field
// devices do not keep a broken stale shell (blank page after deploy).
const updateSW = registerSW({
  immediate: true,
  onNeedRefresh() {
    try {
      // Apply waiting worker then reload; avoids infinite stale HTML + 404 chunks.
      void updateSW(true).then(() => {
        window.location.reload();
      });
    } catch {
      window.location.reload();
    }
  },
  onOfflineReady() {
    // no-op: offline shell ready
  },
  onRegisterError(error) {
    console.warn("Service worker registration failed", error);
  },
});

// Initialize Firebase telemetry as early as possible (lazy + async, never blocking).
// Role/user properties are updated later when auth settles.
void initializeTelemetry();

// Silence repetitive benign warnings emitted by Cloudflare Turnstile's iframe
// (api.js) when it declares experimental/unsupported Permissions-Policy features
// such as xr-spatial-tracking, cross-origin-isolated, etc.
// These are harmless (the browser just skips them) but Firefox is very noisy about them.
const originalWarn = console.warn?.bind(console);
if (originalWarn) {
  console.warn = (...args: any[]) => {
    const first = args[0];
    if (typeof first === "string") {
      if (
        first.includes("Skipping unsupported feature name") &&
        (first.includes("xr-spatial-tracking") ||
          first.includes("cross-origin-isolated") ||
          first.includes("keyboard-map") ||
          first.includes("autoplay"))
      ) {
        return; // drop the spam
      }
    }
    originalWarn(...args);
  };
}

// Initialize client debug logger detection (?debug=true + persisted flag) as early as possible.
// This is a no-op unless the user has explicitly enabled debug mode.
initFromEnvironment();

// Global error handlers — send sanitized errors to backend without sensitive data.
// Also forward to Firebase (Crashlytics-equivalent path when enabled).
window.onerror = (_message, _source, _lineno, _colno, error) => {
  if (error) {
    if (isDexieStorageError(error)) {
      markStorageDegraded(error.message || "window.onerror_storage");
    }
    reportClientError(error);
    recordError(error);
  }
};

window.addEventListener("unhandledrejection", (event) => {
  const reason = event.reason;
  if (reason instanceof Error) {
    if (isDexieStorageError(reason)) {
      markStorageDegraded(reason.message || "unhandledrejection_storage");
      // Prevent noisy Private-mode Dexie rejections from looking like hard failures.
      try {
        event.preventDefault();
      } catch {
        /* ignore */
      }
    }
    reportClientError(reason);
    recordError(reason);
  } else {
    const asStr = String(reason);
    if (isDexieStorageError(asStr) || /null is not an object/i.test(asStr)) {
      markStorageDegraded(asStr);
    }
    reportClientError(asStr);
    recordError(asStr);
  }
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
